import { useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import { VRCertificateTypes } from "@constants/certificates";
import { useNotification } from "@context/NotificationContext";
import type { VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import {
  clearCertificates,
  FORM_FIELDS,
  generateCertificateField,
  isBunkerBargeInProgress as getIsBunkerBargeInProgress,
  getLatestApprovedCertificatesToClear,
} from "@features/vessel-register/utils";
import { VESSEL_STATUS } from "@pages/vessel-management/vessel-status/VesselStatus";
import { getVesselClassificationSocieties, getVesselTypes } from "@services/referenceDataApi";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import type { VesselOutletContext } from "../VesselRegistrationContainer";

const VesselParticulars = () => {
  const { formData, updateFormData, isRestrictedToCertificatesUpdate, vesselDetails } =
    useOutletContext<VesselOutletContext>();
  const { notify } = useNotification();

  const isBunkerBargeAllowed = useMemo(
    () =>
      Boolean(
        vesselDetails?.vesselRegistrationStatus === VESSEL_STATUS.APPROVED ||
          vesselDetails?.bunkerRegistered,
      ),
    [vesselDetails?.vesselRegistrationStatus, vesselDetails?.bunkerRegistered],
  );

  const isBunkerBargeInProgress = getIsBunkerBargeInProgress(vesselDetails);

  const { data: fetchedVesselTypes = [] } = useQuery<
    { vesselTypeCode: string; vesselTypeDesc: string }[]
  >({
    queryKey: ["vessel-types"],
    queryFn: () =>
      getVesselTypes("bff-vessel") as Promise<{ vesselTypeCode: string; vesselTypeDesc: string }[]>,
  });

  const { data: fetchedVesselClassificationSocieties = [] } = useQuery<
    { vesselClassificationSocietyCode: string; vesselClassificationSocietyDesc: string }[]
  >({
    queryKey: ["vessel-classification-societies"],
    queryFn: () =>
      getVesselClassificationSocieties("bff-vessel") as Promise<
        { vesselClassificationSocietyCode: string; vesselClassificationSocietyDesc: string }[]
      >,
  });

  const vesselTypeOptions = fetchedVesselTypes
    .map((vesselType) => ({
      label: vesselType.vesselTypeDesc.toUpperCase(),
      value: vesselType.vesselTypeCode,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const vesselClassificationSocietiesOptions = fetchedVesselClassificationSocieties.map(
    (classificationSociety) => ({
      label: `${classificationSociety.vesselClassificationSocietyCode} - ${classificationSociety.vesselClassificationSocietyDesc.trim()}`,
      value: classificationSociety.vesselClassificationSocietyCode,
    }),
  );

  let bunkerBargeCheckbox = formData.watch(FORM_FIELDS.BUNKER_REGISTERED);
  let diplomaticVesselCheckbox = formData.watch(FORM_FIELDS.DIPLOMATIC_VESSEL);

  const vesselClassificationSociety = formData.watch(FORM_FIELDS.VESSEL_CLASSIFICATION_SOCIETY);

  const isOtherSocietySelected = useMemo(
    () => vesselClassificationSociety === "OT",
    [vesselClassificationSociety],
  );

  const clearLatestApprovedCertificates = () => {
    if (!vesselDetails?.latestApprovedCertificates?.length) return;

    const certsToClear = getLatestApprovedCertificatesToClear(
      formData.getValues(FORM_FIELDS.CERTIFICATES),
      vesselDetails?.latestApprovedCertificates ?? [],
    );

    if (certsToClear.length) {
      notify({
        type: "warning",
        title: t("vessel-management.notifications.this-will-invalidate-certificates"),
      });
      clearCertificates(formData.setValue, certsToClear, updateFormData);
    }
  };

  return (
    <div data-testid="vessels-type-page" className="pb-8">
      <FormProvider {...formData}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.VESSEL_TYPE_CODE>
              testId="register-vessel-type-code-autocomplete"
              name={FORM_FIELDS.VESSEL_TYPE_CODE}
              label={t("vessel-management.vessel-type-code")}
              type="autocomplete"
              options={vesselTypeOptions}
              onFieldChange={(k, v) => {
                Object.values(VRCertificateTypes).forEach((certType) => {
                  formData.clearErrors(generateCertificateField.url(certType));
                  formData.clearErrors(generateCertificateField.expiryDate(certType));
                  formData.clearErrors(generateCertificateField.className(certType));
                });
                updateFormData(k, v);
              }}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1 lg:col-span-2 flex justify-start lg:justify-end">
            <div className="flex items-end gap-4">
              <div className="flex items-center gap-2 whitespace-nowrap">
                <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.DIPLOMATIC_VESSEL>
                  name={FORM_FIELDS.DIPLOMATIC_VESSEL}
                  type="checkbox"
                  label={t("vessel-management.diplomatic-vessel")}
                  testId="diplomatic-vessel-checkbox"
                  onFieldChange={(key, value) => {
                    updateFormData(key, value);
                    if (value) {
                      formData.clearErrors(FORM_FIELDS.DIPLOMATIC_VESSEL);
                    } else {
                      formData.trigger(FORM_FIELDS.DIPLOMATIC_VESSEL);
                    }
                  }}
                  defaultValue={diplomaticVesselCheckbox}
                  disabled={isRestrictedToCertificatesUpdate}
                />
              </div>
              {isBunkerBargeAllowed && (
                <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.BUNKER_REGISTERED>
                  name={FORM_FIELDS.BUNKER_REGISTERED}
                  type="checkbox"
                  label={t("vessel-management.bunker-barge")}
                  testId="bunker-barge-checkbox"
                  onFieldChange={(key, value) => {
                    updateFormData(key, value);
                    if (value) {
                      formData.clearErrors(FORM_FIELDS.BUNKER_REGISTERED);
                    } else {
                      formData.trigger(FORM_FIELDS.BUNKER_REGISTERED);
                    }
                  }}
                  defaultValue={bunkerBargeCheckbox}
                  disabled={isRestrictedToCertificatesUpdate || isBunkerBargeInProgress}
                />
              )}
            </div>
          </div>

          <div className="col-span-1 lg:col-span-3 h-0" />
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.GROSS_TONNAGE>
              testId="register-gross-tonnage-input"
              name={FORM_FIELDS.GROSS_TONNAGE}
              type="number"
              label={t("vessel-management.gross-tonnage")}
              decimals={3}
              maxDigits={15}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.NET_TONNAGE>
              testId="register-net-tonnage-input"
              name={FORM_FIELDS.NET_TONNAGE}
              type="number"
              label={t("vessel-management.net-tonnage")}
              decimals={3}
              maxDigits={15}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.DEADWEIGHT_TONNAGE>
              testId="register-deadweight-tonnage-input"
              name={FORM_FIELDS.DEADWEIGHT_TONNAGE}
              type="number"
              label={t("vessel-management.deadweight-tonnage")}
              decimals={3}
              maxDigits={11}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.LENGTH_OVERALL>
              testId="register-length-overall-input"
              name={FORM_FIELDS.LENGTH_OVERALL}
              type="number"
              label={t("vessel-management.length-overall")}
              decimals={3}
              maxDigits={15}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.DRAFT_MAX>
              testId="register-draft-max-input"
              name={FORM_FIELDS.DRAFT_MAX}
              type="number"
              label={t("vessel-management.maxDraft")}
              decimals={3}
              maxDigits={15}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.BEAM>
              testId="register-beam-input"
              name={FORM_FIELDS.BEAM}
              type="number"
              label={t("vessel-management.beam")}
              decimals={3}
              maxDigits={15}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>

          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.VESSEL_CLASSIFICATION_SOCIETY>
              testId="register-vessel-classification-society-autocomplete"
              name={FORM_FIELDS.VESSEL_CLASSIFICATION_SOCIETY}
              label={t("vessel-management.vessel-classification-society")}
              type="autocomplete"
              options={vesselClassificationSocietiesOptions}
              onFieldChange={(key, value) => {
                clearLatestApprovedCertificates();
                updateFormData(key, value);
                if (value !== "OT") {
                  formData.setValue(FORM_FIELDS.OTHER_VESSEL_CLASSIFICATION_SOCIETY, "", {
                    shouldDirty: true,
                    shouldValidate: false,
                  });
                  updateFormData(FORM_FIELDS.OTHER_VESSEL_CLASSIFICATION_SOCIETY, "");
                  formData.clearErrors(FORM_FIELDS.OTHER_VESSEL_CLASSIFICATION_SOCIETY);
                }
              }}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          {isOtherSocietySelected && (
            <div className="col-span-1">
              <FormItem<
                VesselRegisterFormData,
                typeof FORM_FIELDS.OTHER_VESSEL_CLASSIFICATION_SOCIETY
              >
                testId="register-vessel-other-classification-society-input"
                label={t("vessel-management.other-vessel-classification-society")}
                name={FORM_FIELDS.OTHER_VESSEL_CLASSIFICATION_SOCIETY}
                type="text"
                maxChars={30}
                onFieldChange={updateFormData}
                disabled={isRestrictedToCertificatesUpdate}
              />
            </div>
          )}
        </div>
      </FormProvider>
    </div>
  );
};

export default VesselParticulars;
